import React from "react";
import { useDropzone } from "react-dropzone";
import { MdDelete, MdCloudUpload } from "react-icons/md";
import { useGlobalAIDrawers } from '../hooks/useGlobalAIDrawers';

export interface MediaUploadProps {
  type: "image" | "video";
  value?: string; // Controlled media URL
  onChange: (url: string) => void; // Controlled updater
  onFileUpload?: (file: File) => void; // File selected -> parent handles upload
  label?: string;
  uploading?: boolean;
  error?: string;
  enableAIGeneration?: boolean; // Enable AI image generation
  aiPromptContext?: string; // Context for AI generation (e.g., "restaurant banner", "food image")
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  type,
  value,
  onChange,
  onFileUpload,
  label = "Upload Media",
  uploading = false,
  error,
  enableAIGeneration = true,
  aiPromptContext = "",
}) => {
  /** ---------- Global AI Drawer Hook ---------- */
  const { openImageDrawer } = useGlobalAIDrawers();

  /** ---------- File Config ---------- */
  const acceptedTypes =
    type === "image"
      ? ["image/jpeg", "image/png", "image/gif", "image/webp"]
      : ["video/mp4", "video/webm", "video/ogg", "video/quicktime"];

  const maxFileSize = type === "image" ? 10 * 1024 * 1024 : 50 * 1024 * 1024;

  /** ---------- Dropzone Setup ---------- */
  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept: acceptedTypes.reduce((acc, t) => ({ ...acc, [t]: [] }), {}),
      maxSize: maxFileSize,
      multiple: false,
      disabled: uploading,
      onDropAccepted: (files) => {
        const file = files[0];
        if (file && onFileUpload) onFileUpload(file);
      },
    });

  /** ---------- Delete Media ---------- */
  const handleDelete = () => onChange("");

  /** ---------- AI Drawer Handlers ---------- */
  const handleOpenAIDrawer = () => {
    openImageDrawer(
      aiPromptContext,
      (imageUrl: string) => {
        onChange(imageUrl);
      }
    );
  };

  return (
    <>
      <div className="space-y-2 w-full" aria-label={label} role="group">
        {/* Label */}
        <label className="block text-sm font-medium text-gray-700">{label}</label>

        {/* Manual URL Input */}
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter ${type} URL or upload`}
          className="w-full rounded-lg border p-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          disabled={uploading}
        />

        {/* AI Generation Button - Only show for images */}
        {enableAIGeneration && type === "image" && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleOpenAIDrawer}
              disabled={uploading}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Generate with AI
            </button>
          </div>
        )}

        {/* Drag & Drop Zone */}
        <div
          {...getRootProps()}
          className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${
            isDragActive
              ? "bg-red-50"
              : "border-gray-300 bg-white hover:bg-red-25"
          } ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          style={{
            borderColor: isDragActive ? "#EF4421" : undefined
          }}
        >
          <input {...getInputProps()} />
          
          {uploading ? (
            <div className="flex flex-col items-center space-y-3">
              {/* Loading Spinner */}
              <div className="relative">
                <div className="w-12 h-12 border-4 border-red-200 rounded-full animate-spin" style={{ borderTopColor: "#EF4421" }}></div>
                <MdCloudUpload className="absolute inset-0 m-auto w-6 h-6" style={{ color: "#EF4421" }} />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium" style={{ color: "#EF4421" }}>Uploading {type}...</p>
                <p className="text-xs text-gray-500">Please wait while we process your file</p>
              </div>
              {/* Progress Bar */}
              <div className="w-full max-w-xs bg-gray-200 rounded-full h-2">
                <div className="h-2 rounded-full animate-pulse" style={{ backgroundColor: "#EF4421", width: '70%' }}></div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-3">
              <MdCloudUpload className="w-12 h-12 text-gray-400" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">
                  {isDragActive ? `Drop your ${type} here` : `Upload ${type}`}
                </p>
                <p className="text-xs text-gray-500">
                  Drag & drop or click to browse files
                </p>
                <p className="text-xs text-gray-400">
                  {type === "image" ? "PNG, JPG, GIF up to 10MB" : "MP4, WebM up to 50MB"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        {value && (
          <div className="relative inline-block">
            {uploading && (
              <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg z-10">
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-8 h-8 border-3 border-red-200 rounded-full animate-spin" style={{ borderTopColor: "#EF4421" }}></div>
                  <p className="text-xs font-medium" style={{ color: "#EF4421" }}>Processing...</p>
                </div>
              </div>
            )}
            
            {type === "image" ? (
              <img
                src={value}
                alt="Preview"
                className={`max-h-48 max-w-full mx-auto rounded-lg border object-cover transition-opacity duration-200 ${
                  uploading ? "opacity-50" : "opacity-100"
                }`}
              />
            ) : (
              <video
                src={value}
                controls
                className={`max-h-48 max-w-full mx-auto rounded-lg bg-black transition-opacity duration-200 ${
                  uploading ? "opacity-50" : "opacity-100"
                }`}
                preload="metadata"
              />
            )}
            
            <button
              type="button"
              onClick={handleDelete}
              className={`absolute top-2 right-2 bg-white rounded-full shadow-lg p-2 text-red-500 hover:bg-red-50 hover:text-red-600 transition-all duration-200 ${
                uploading ? "opacity-50 cursor-not-allowed" : "opacity-100"
              }`}
              aria-label="Delete media"
              disabled={uploading}
              title="Remove media"
            >
              <MdDelete size={18} />
            </button>
          </div>
        )}

        {/* Errors */}
        {error && (
          <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        
        {fileRejections.length > 0 && (
          <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{fileRejections[0].errors[0]?.message || "Invalid file"}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(MediaUpload);
